import { FC } from "react";
import { useNavigate } from "react-router";

import {
  Menu,
  MenuButton,
  MenuItem,
  MenuItems,
  Transition,
} from "@headlessui/react";
import { CircleUser } from "lucide-react";

import { useAuth } from "../auth";

import { mergeClassNames } from "../helpers";
import { useAppSelector } from "../store";

import { TOP_MENU_ICON_WRAPPER } from "../components/Navigation/styles";
import { LARGE_ICON_SIZE_STYLES } from "../ui/styles";
import { opacityScaleTransition } from "../ui/layout/Transition/constants";
import { useTheme } from "../ui/layout";

export const UserAvatar: FC<React.PropsWithChildren> = () => {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const {
    id: userId,
    email: userEmail,
    emailVerified,
  } = useAppSelector((state) => state.user);
  const { companies } = useAppSelector((state) => state.companies);

  const userProfileLink = `/users/show/${userId}`;

  const { switchTheme } = useTheme();

  const userNavigation = [
    { name: "Sign out", href: "#", onClick: () => logout() },
    { name: "Profile", href: "#", onClick: () => navigate(userProfileLink) },
    {
      name: "Switch theme",
      href: "#",
      onClick: () => switchTheme(),
      mobileOnly: true,
    },
  ];

  return (
    <Menu as="div" className="relative mr-2 md:mr-0">
      <div>
        <MenuButton className="flex items-center ">
          <span className="sr-only">Open user menu</span>
          <div className={TOP_MENU_ICON_WRAPPER}>
            <CircleUser className={LARGE_ICON_SIZE_STYLES} />
          </div>
        </MenuButton>
      </div>
      <Transition as="div" {...opacityScaleTransition}>
        <MenuItems className="absolute right-0 mt-2 w-60 origin-top-right rounded-md bg-stone-50 py-1 ring-1 ring-black/5 focus:outline-none dark:bg-stone-700">
          {userEmail && (
            <div className="flex flex-col items-center border-b border-stone-100 py-4  text-stone-700 dark:border-stone-600">
              <div className="flex h-12 w-12 items-center justify-center rounded-3xl bg-stone-100 dark:bg-stone-900">
                <CircleUser className="h-8 w-8 text-stone-400" />
              </div>
              <h2 className="mt-2 text-base dark:text-white">Profile</h2>
              <div className="text-xs dark:text-stone-300">
                {userEmail}
                {emailVerified && <span className="mx-1">✓</span>}
              </div>
            </div>
          )}

          {userNavigation.map(({ name, onClick, mobileOnly }) => {
            if (name === "Profile" && (!companies || companies.length === 0))
              return null;

            return (
              <MenuItem key={name}>
                {({ focus }) => (
                  <button
                    onClick={onClick}
                    className={mergeClassNames(
                      focus ? " bg-stone-100 dark:bg-stone-800" : "",
                      "w-full cursor-pointer px-4 py-2 text-left text-stone-700 first:block dark:text-stone-200",
                      mobileOnly ? "sm:hidden" : "",
                    )}
                  >
                    {name}
                  </button>
                )}
              </MenuItem>
            );
          })}
        </MenuItems>
      </Transition>
    </Menu>
  );
};
